import { createApi } from '@reduxjs/toolkit/query/react';
import { DEEP_ANALYSIS_API_URL } from '../settings/config';
import { authRequest } from '../store/authorizedRequests';
import { DomainContentAnalysis, DomainVisibilityAnalysis, DomainContentAnalysisProcess, DomainVisibilityAnalysisProcess, WebpageVisualSeoAnalysis, WebpageVisualSeoAnalysisProcess } from './types';

const visualSeoActiveProcessId = (wGuid: string, url: string) => `${wGuid}_${url}_visual_seo_analysis`;

const llmToolsBaseUrl = `${DEEP_ANALYSIS_API_URL}`;

const RESOURCE_REFETCH_TIME = 7200;

export const llmToolsApi = createApi({
    reducerPath: "api.llm-tools",
    keepUnusedDataFor: RESOURCE_REFETCH_TIME,
    tagTypes: [
        "DomainContentAnalysis",
        "DomainVisibilityAnalysis",
        "WebpageVisualSeoAnalysis",
        "ActiveProcesses",
    ],

    baseQuery: async (args, api, extraOptions) => {
        const { url, method, body, signal } = args;
        const { dispatch, getState } = api;

        try {
            const data = await authRequest(llmToolsBaseUrl.concat(url), dispatch, getState, {
                method: method,
                body: JSON.stringify(body),
                signal: signal,
            });

            return { data };
        } catch (error) {
            return { error };
        }
    },

    endpoints: (builder) => ({
        fetchDomainContentAnalysis: builder.query<DomainContentAnalysis, { domain: string }>({
            query: (args) => {
                const { domain } = args;

                return {
                    method: 'GET',
                    url: `/content-analysis?domain=${encodeURIComponent(domain)}`,
                };
            },
            providesTags: ['DomainContentAnalysis'],
        }),

        createDomainContentAnalysisProcess: builder.mutation<{ guid: string }, { domain: string }>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/content-analysis',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_content_analysis` }],
        }),

        fetchDomainContentAnalysisProcess: builder.query<DomainContentAnalysisProcess, { domain: string, processGuid: string | null }>({
            query: (args) => {
                const { domain, processGuid } = args;
                const endpoint = processGuid ? `/content-analysis/active?domain=${encodeURIComponent(domain)}&process_guid=${processGuid}` : `/content-analysis/active?domain=${encodeURIComponent(domain)}`;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_content_analysis` }],
        }),

        fetchDomainVisibilityAnalysis: builder.query<DomainVisibilityAnalysis, { domain: string }>({
            query: (args) => {
                const { domain } = args;

                return {
                    method: 'GET',
                    url: `/visibility-analysis?domain=${encodeURIComponent(domain)}`,
                };
            },
            providesTags: ['DomainVisibilityAnalysis']
        }),

        createDomainVisibilityAnalysisProcess: builder.mutation<{ guid: string }, { domain: string }>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/visibility-analysis',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_visibility_analysis` }],
        }),

        fetchDomainVisibilityAnalysisProcess: builder.query<DomainVisibilityAnalysisProcess, { domain: string, processGuid: string | null }>({
            query: (args) => {
                const { domain, processGuid } = args;
                const endpoint = processGuid ? `/visibility-analysis/active?domain=${encodeURIComponent(domain)}&process_guid=${processGuid}` : `/visibility-analysis/active?domain=${encodeURIComponent(domain)}`;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_visibility_analysis` }],
        }),

        fetchWebpageVisualSeoAnalysis: builder.query<WebpageVisualSeoAnalysis, { wGuid: string, url: string }>({
            query: (args) => {
                const { wGuid, url } = args;

                return {
                    method: 'GET',
                    url: `/visual-seo-analysis?w_guid=${encodeURIComponent(wGuid)}&url=${encodeURIComponent(url)}`,
                };
            },
            providesTags: ['WebpageVisualSeoAnalysis'],
        }),

        createWebpageVisualSeoAnalysisProcess: builder.mutation<{ guid: string, c_at: string }, { wGuid: string, url: string }>({
            query: (args) => {
                const { wGuid, url } = args;

                return {
                    method: 'POST',
                    body: { w_guid: wGuid, url },
                    url: '/visual-seo-analysis',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: visualSeoActiveProcessId(args.wGuid, args.url) }],
        }),

        fetchWebpageVisualSeoAnalysisProcess: builder.query<WebpageVisualSeoAnalysisProcess, { wGuid: string, url: string, processGuid: string | null }>({
            query: (args) => {
                const { wGuid, url, processGuid } = args;
                const baseUrl = `/visual-seo-analysis/active?w_guid=${encodeURIComponent(wGuid)}&url=${encodeURIComponent(url)}`;
                const endpoint = processGuid ? `${baseUrl}&process_guid=${processGuid}` : baseUrl;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: visualSeoActiveProcessId(args.wGuid, args.url) }],
        }),

    })
});

export const {
    useFetchDomainContentAnalysisQuery,
    useLazyFetchDomainContentAnalysisQuery,
    useFetchDomainContentAnalysisProcessQuery,
    useCreateDomainContentAnalysisProcessMutation,

    useFetchDomainVisibilityAnalysisQuery,
    useLazyFetchDomainVisibilityAnalysisQuery,
    useFetchDomainVisibilityAnalysisProcessQuery,
    useCreateDomainVisibilityAnalysisProcessMutation,

    useFetchWebpageVisualSeoAnalysisQuery,
    useLazyFetchWebpageVisualSeoAnalysisQuery,
    useCreateWebpageVisualSeoAnalysisProcessMutation,
    useFetchWebpageVisualSeoAnalysisProcessQuery,
} = llmToolsApi;

